import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { finalize } from 'rxjs';
import { ERecipientTypeEnum } from '../../../enums/recipient-type.enum';
import { EUserType } from '../../../enums/user-type.enum';
import {
  IAuthEngineHttpBaseResponse,
  ICheckPasswordResetOtpTokenResponseData,
  IResetPasswordRequest,
  IRequestPasswordResetTokenRequest,
} from '../../../interfaces';
import { AuthService } from '../../../services/auth.service';
import {ExtractHttpErrorResponseCode, ExtractHttpErrorResponseMessage} from "@zikata-frontend/util";

@Component({
  selector: 'zikata-frontend-common-reset-password',
  templateUrl: './common-reset-password.component.html'
})
export class CommonResetPasswordComponent implements OnInit {
  @Input() authorizationServiceBaseUrl!: string;
  @Input() authorizationClientId!: string;

  @Output() OnGoToLogin: EventEmitter<boolean> = new EventEmitter();

  resetRequestStatus?: 'tokenRequest' | 'tokenRequestSent' | 'passwordReset' | 'success' | 'failure';
  isSubmitting = false;
  isVerifyingToken = true;
  oneTimePasswordToken: string;
  noReceiveQuestion = "Didn't receive the email";
  failureTitle = '';
  failureMessage = '';
  recipient?: string;
  showResendLink = false;

  constructor(private authService: AuthService, private activatedRoute: ActivatedRoute) {
    this.oneTimePasswordToken = this.activatedRoute.snapshot.queryParamMap.get('token') ?? '';
  }

  ngOnInit(): void {
    if (this.oneTimePasswordToken) {
      this.checkActivationOtp();
    } else {
      this.isVerifyingToken = false;
      this.resetRequestStatus = 'tokenRequest';
    }
  }

  checkActivationOtp(): void {
    this.authService
      .checkPasswordResetOtp(this.authorizationServiceBaseUrl, this.oneTimePasswordToken, EUserType.CLIENT)
      .pipe(finalize(() => (this.isVerifyingToken = false)))
      .subscribe({
        next: (response: IAuthEngineHttpBaseResponse<ICheckPasswordResetOtpTokenResponseData>) => {
          if (response.data) {
            this.resetRequestStatus = 'passwordReset';
            return;
          }
          this.resetRequestStatus = 'failure';
        },
        error: (error: HttpErrorResponse) => {
          this.failureTitle = 'Link check failed';
          this.failureMessage = ExtractHttpErrorResponseMessage(error, 'Could not validate reset link');
          this.showResendLink = false;

          if (ExtractHttpErrorResponseCode(error) === 'OTP_EXPIRED') {
            this.failureTitle = 'Link Expired';
            this.showResendLink = true;
          }

          this.resetRequestStatus = 'failure';
        },
      });
  }

  onGoToLogin(): void {
    this.OnGoToLogin.emit(true);
  }

  onEmailFormSubmit($event: string) {
    this.recipient = $event;
    const requestPasswordToken: IRequestPasswordResetTokenRequest = {
      username: $event,
      usernameType: ERecipientTypeEnum.EMAIL_ADDRESS,
      clientId: this.authorizationClientId,
      userType: EUserType.CLIENT,
    };

    this.isSubmitting = true;

    this.authService
      .requestPasswordResetToken(this.authorizationServiceBaseUrl, requestPasswordToken)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.resetRequestStatus = 'tokenRequestSent';
        },
        error: (error: HttpErrorResponse) => {
          this.failureTitle = 'Password Reset Request Failed';
          this.failureMessage = ExtractHttpErrorResponseMessage(error, 'Could not request for a password reset link');
          this.resetRequestStatus = 'failure';
        },
      });
  }

  onPasswordFormSubmit($event: string) {
    const requestPasswordRequest: IResetPasswordRequest = {
      oneTimePasswordToken: this.oneTimePasswordToken || '',
      newPassword: $event,
      clientId: this.authorizationClientId,
      userType: EUserType.CLIENT,
    };

    this.isSubmitting = true;

    this.authService
      .resetPassword(this.authorizationServiceBaseUrl, requestPasswordRequest)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.resetRequestStatus = 'success';
        },
        error: error => {
          this.failureTitle = 'Request Password Failed';
          this.failureMessage = ExtractHttpErrorResponseMessage(error, 'Could not reset password');
          this.resetRequestStatus = 'failure';
        },
      });
  }

  resetToRequestNewToken() {
    this.resetRequestStatus = 'tokenRequest';
  }

}
